import { App } from 'antd';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getActiveUnsavedChangesEntry,
  hasUnsavedChangesInRegistry,
  registerUnsavedChangesEntry,
} from '../../lib/unsavedChanges/unsavedChangesRegistry';

import type {
  RegisterUnsavedChangesOptions,
  UnsavedChangesContextValue,
} from './unsavedChangesContextTypes';

/** Shared register / requestLeave logic for root and modal providers. */
export function useUnsavedChangesContextValue(): UnsavedChangesContextValue {
  const { modal } = App.useApp();
  const { t } = useTranslation();
  const pendingConfirmationRef = useRef<Promise<boolean> | null>(null);
  const registryVersionRef = useRef(0);

  const bumpRegistryVersion = useCallback(() => {
    registryVersionRef.current += 1;
  }, []);

  const hasUnsavedChanges = useCallback((): boolean => {
    return hasUnsavedChangesInRegistry();
  }, []);

  const showConfirmDialog = useCallback((): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      let settled = false;

      const resolveOnce = (value: boolean) => {
        if (settled) {
          return;
        }

        settled = true;
        resolve(value);
      };

      modal.confirm({
        cancelText: t('common.cancel'),
        content: t('errors.unsavedChangesMessage'),
        okCancel: true,
        okText: t('common.confirm'),
        onCancel: () => resolveOnce(false),
        onOk: () => resolveOnce(true),
        title: t('errors.unsavedChangesTitle'),
      });
    });
  }, [modal, t]);

  const requestLeave = useCallback(async (): Promise<boolean> => {
    const activeEntry = getActiveUnsavedChangesEntry();

    if (!activeEntry) {
      return true;
    }

    if (pendingConfirmationRef.current) {
      return pendingConfirmationRef.current;
    }

    const confirmationPromise = (async () => {
      const confirmed = await showConfirmDialog();

      if (confirmed) {
        activeEntry.onDiscard?.();
        bumpRegistryVersion();
      }

      return confirmed;
    })();

    pendingConfirmationRef.current = confirmationPromise;

    try {
      return await confirmationPromise;
    } finally {
      pendingConfirmationRef.current = null;
    }
  }, [bumpRegistryVersion, showConfirmDialog]);

  const register = useCallback((options: RegisterUnsavedChangesOptions): (() => void) => {
    const registrationId = options.id ?? Symbol('unsavedChangesRegistration');

    return registerUnsavedChangesEntry({
      id: registrationId,
      isDirty: options.isDirty,
      onDiscard: options.onDiscard,
      scope: options.scope,
    });
  }, []);

  return {
    hasUnsavedChanges,
    register,
    requestLeave,
  };
}
